import type {
  TriggerMatchConfig,
  TriggerCondition,
  ConditionOperator,
  CrmOperation,
  RelativeDateUnit,
  RelativeDateDirection,
  RelativeDateOperator,
  OperatorOption,
  InstalledAppOption,
  AppEventOption,
  AppEntityOption,
  ThreadEntityOption,
  TriggerMenuItem,
  CrmFieldOption,
} from './types'

/** Thread kinds from Prisma ThreadKind (primary: Conversation) */
export const THREAD_KINDS = [
  { value: 'CONVERSATION', label: 'Conversation' },
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'ESCALATION', label: 'Escalation' },
  { value: 'BROADCAST', label: 'Broadcast' },
  { value: 'TESTING', label: 'Testing' },
] as const

/** Participant kinds used in message event filters */
export const PARTICIPANT_KINDS = [
  { value: 'CONTACT', label: 'Contact' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'WORKFLOW', label: 'Workflow' },
  { value: 'AGENT', label: 'Agent' },
] as const

export const THREAD_KIND_PATH = 'data.thread.kind'
export const PARTICIPANT_KIND_PATH = 'data.participant.kind'
export const ATTACHMENT_RECEIVED_EVENT = 'thread.attachment.received'

/** Thread events only fire on threads where the workflow/agent is a participant. */
export function isThreadTrigger(
  matchConfig?: { source?: string; eventType?: string } | null,
  eventType?: string | null,
): boolean {
  if (matchConfig?.source === 'thread') return true
  if (matchConfig?.source === 'crm' || matchConfig?.source === 'app') {
    return false
  }
  const type = eventType ?? matchConfig?.eventType ?? ''
  return type.startsWith('thread.')
}

/**
 * Sentence for a subscription row. Prefers matchConfig; falls back to event type.
 */
export function triggerSentenceFromSubscription(sub: {
  matchConfig?: TriggerMatchConfig | null
  eventType?: string | null
}): string {
  if (sub.matchConfig) {
    return buildSentence(sub.matchConfig)
  }
  const type = sub.eventType ?? ''
  const labeled = THREAD_EVENTS.find((event) => event.value === type)
  if (labeled) {
    return `When ${labeled.label.toLowerCase()}`
  }
  return type.length > 0 ? `When ${type}` : 'When an event occurs'
}

/** Enabled thread-source recipes that will arm when the workflow/agent joins a thread. */
export function threadTriggerPreviewSentences(
  subscriptions: Array<{
    matchConfig?: TriggerMatchConfig | null
    eventType?: string | null
    isEnabled?: boolean
  }>,
): string[] {
  return subscriptions
    .filter(
      (sub) =>
        sub.isEnabled !== false &&
        isThreadTrigger(sub.matchConfig, sub.eventType),
    )
    .map((sub) => triggerSentenceFromSubscription(sub))
}

const PRIMARY_THREAD_PATHS = new Set([THREAD_KIND_PATH, PARTICIPANT_KIND_PATH])

// Thread entities shown in the Add Trigger submenu
export const THREAD_ENTITIES: ThreadEntityOption[] = [
  {
    id: 'thread',
    label: 'Thread',
    icon: 'MessageSquarePlus',
    operations: [
      { value: 'created', label: 'created', eventType: 'thread.created' },
    ],
  },
  {
    id: 'message',
    label: 'Message',
    icon: 'MessageSquare',
    operations: [
      {
        value: 'received',
        label: 'received',
        eventType: 'thread.message.received',
      },
      { value: 'sent', label: 'sent', eventType: 'thread.message.sent' },
    ],
  },
  {
    id: 'attachment',
    label: 'Attachment',
    icon: 'Paperclip',
    operations: [
      {
        value: 'received',
        label: 'received',
        eventType: ATTACHMENT_RECEIVED_EVENT,
      },
    ],
  },
]

/** @deprecated Use THREAD_ENTITIES — kept for legacy summary fallbacks */
export const THREAD_EVENTS = [
  {
    value: 'thread.message.received',
    label: 'Message Received',
    icon: 'MessageSquare' as const,
    description: 'When a message is received in a thread',
  },
  {
    value: 'thread.message.sent',
    label: 'Message Sent',
    icon: 'Send' as const,
    description: 'When a message is sent in a thread',
  },
  {
    value: 'thread.created',
    label: 'Thread Created',
    icon: 'MessageSquarePlus' as const,
    description: 'When a new thread is created',
  },
  {
    value: ATTACHMENT_RECEIVED_EVENT,
    label: 'Attachment Received',
    icon: 'Paperclip' as const,
    description: 'When a file is attached to a thread message',
  },
]

// CRM operation labels
export const CRM_OPERATION_LABELS: Record<CrmOperation, string> = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
}

export const DEFAULT_CRM_OPERATION_OPTIONS: Array<{
  value: CrmOperation
  label: string
}> = (Object.entries(CRM_OPERATION_LABELS) as Array<[CrmOperation, string]>).map(
  ([value, label]) => ({ value, label }),
)

/**
 * Stable key for TriggerSentenceList catalog reloads when Live Version changes.
 * - followLatest → `latest` (server resolves tip YAML via resolveLiveWorkflowVersionId)
 * - pinned version → `pin:{workflowVersionId}`
 * - deactivated / no version → `none`
 */
export function buildTriggerCatalogKey(params: {
  followLatest?: boolean | null
  workflowVersionId?: string | null
}): string {
  if (params.followLatest) return 'latest'
  const pinned = params.workflowVersionId?.trim()
  if (pinned) return `pin:${pinned}`
  return 'none'
}

/**
 * Chip options for CRM/app operation selects.
 * When YAML `@crm/...` / `@app/...` inputs constrain ops, pass those;
 * otherwise fall back to the full created/updated/deleted set.
 */
export function resolveOperationChipOptions(
  availableOperations?: Array<{ value: string; label: string }> | null,
  fallback: Array<{ value: string; label: string }> = DEFAULT_CRM_OPERATION_OPTIONS,
): Array<{ value: string; label: string }> {
  if (availableOperations && availableOperations.length > 0) {
    return availableOperations.map((op) => ({
      value: op.value,
      label: op.label,
    }))
  }
  return fallback
}

/** Prefer current op when allowed; otherwise first allowed option. */
export function coerceOperationValue(
  current: string | null | undefined,
  options: Array<{ value: string; label: string }>,
): string {
  if (current && options.some((o) => o.value === current)) {
    return current
  }
  return options[0]?.value ?? current ?? 'updated'
}

/** Copula for the primary CRM/app clause: deleted → was, else is */
export function primaryCopula(operation?: string | null): 'is' | 'was' {
  return operation === 'deleted' ? 'was' : 'is'
}

// Operator options for field conditions
export const FIELD_OPERATORS: OperatorOption[] = [
  { value: 'eq', label: 'is', requiresValue: true },
  { value: 'neq', label: 'is not', requiresValue: true },
  { value: 'gt', label: 'is greater than', requiresValue: true },
  { value: 'gte', label: 'is at least', requiresValue: true },
  { value: 'lt', label: 'is less than', requiresValue: true },
  { value: 'lte', label: 'is at most', requiresValue: true },
  { value: 'contains', label: 'contains', requiresValue: true },
  { value: 'changed_from_to', label: 'is changed from', requiresFromTo: true },
]

// Relative date operators (preferred before/after)
export const RELATIVE_DATE_OPERATORS: OperatorOption[] = [
  { value: 'before', label: 'before', requiresValue: true },
  { value: 'after', label: 'after', requiresValue: true },
]

// Relative date unit labels
export const RELATIVE_DATE_UNIT_LABELS: Record<RelativeDateUnit, string> = {
  minutes: 'minutes ago',
  hours: 'hours ago',
  days: 'days ago',
  weeks: 'weeks ago',
}

// Common relative date presets (past + future)
export const RELATIVE_DATE_PRESETS = [
  {
    amount: 1,
    unit: 'hours' as const,
    direction: 'past' as const,
    label: '1 hour ago',
  },
  {
    amount: 1,
    unit: 'days' as const,
    direction: 'past' as const,
    label: '1 day ago',
  },
  {
    amount: 1,
    unit: 'weeks' as const,
    direction: 'past' as const,
    label: '1 week ago',
  },
  {
    amount: 2,
    unit: 'weeks' as const,
    direction: 'past' as const,
    label: '2 weeks ago',
  },
  {
    amount: 4,
    unit: 'weeks' as const,
    direction: 'past' as const,
    label: '1 month ago',
  },
  {
    amount: 1,
    unit: 'days' as const,
    direction: 'future' as const,
    label: '1 day from now',
  },
  {
    amount: 1,
    unit: 'weeks' as const,
    direction: 'future' as const,
    label: '1 week from now',
  },
  {
    amount: 2,
    unit: 'weeks' as const,
    direction: 'future' as const,
    label: '2 weeks from now',
  },
]

export function getOperatorLabel(op: ConditionOperator | string): string {
  const found = [...FIELD_OPERATORS, ...RELATIVE_DATE_OPERATORS].find(
    (o) => o.value === op,
  )
  return found?.label ?? op
}

export function formatRelativeDate(
  amount: number,
  unit: RelativeDateUnit,
  direction: RelativeDateDirection = 'past',
): string {
  const unitLabel =
    unit === 'minutes'
      ? amount === 1
        ? 'minute'
        : 'minutes'
      : unit === 'hours'
        ? amount === 1
          ? 'hour'
          : 'hours'
        : unit === 'days'
          ? amount === 1
            ? 'day'
            : 'days'
          : amount === 1
            ? 'week'
            : 'weeks'
  if (direction === 'future') {
    return `${amount} ${unitLabel} from now`
  }
  return `${amount} ${unitLabel} ago`
}

/** Normalize legacy lt/gt relative ops to before/after for display */
export function toBeforeAfterOp(
  op: RelativeDateOperator,
): 'before' | 'after' {
  if (op === 'before' || op === 'after') return op
  // Legacy: gt = older = before threshold; lt = newer = after threshold
  if (op === 'gt' || op === 'gte') return 'before'
  return 'after'
}

export function isDateField(field: CrmFieldOption): boolean {
  return (
    field.type === 'DATE' ||
    field.type === 'DATE_TIME' ||
    field.handle.endsWith('_at') ||
    field.handle.includes('date')
  )
}

function normalizeFieldType(type: string): string {
  return type.trim().toUpperCase()
}

export function isNumberField(field: CrmFieldOption): boolean {
  return normalizeFieldType(field.type) === 'NUMBER'
}

export function isBooleanField(field: CrmFieldOption): boolean {
  return normalizeFieldType(field.type) === 'BOOLEAN'
}

export type TriggerValueKind = 'enum' | 'boolean' | 'number' | 'string'

export function fieldHasOptions(field: CrmFieldOption): boolean {
  return field.hasOptions || (field.options?.length ?? 0) > 0
}

export function valueKindForField(
  field: CrmFieldOption | undefined,
): TriggerValueKind {
  if (!field) return 'string'
  if (fieldHasOptions(field)) return 'enum'
  if (isNumberField(field)) return 'number'
  if (isBooleanField(field)) return 'boolean'
  return 'string'
}

export function findCrmField(
  fields: CrmFieldOption[],
  pathOrHandle: string,
): CrmFieldOption | undefined {
  const exact = fields.find((f) => f.handle === pathOrHandle)
  if (exact) return exact
  const handle = fieldHandleFromPath(pathOrHandle)
  return fields.find(
    (f) => f.handle === handle || fieldHandleFromPath(f.handle) === handle,
  )
}

/** True when the field handle is already a payload path (thread/app context). */
export function isEventPayloadPath(handle: string): boolean {
  return (
    handle.startsWith('data.') ||
    handle.startsWith('resource.') ||
    handle.startsWith('actor.') ||
    handle.startsWith('meta.')
  )
}

/**
 * Condition path for a picker field. Thread/event-context handles are already
 * payload paths (`data.attachment.name`); CRM handles are remapped.
 */
export function conditionPathForField(
  handle: string,
  operation?: string | null,
): string {
  if (isEventPayloadPath(handle)) return handle
  return crmFieldPayloadPath(handle, operation)
}

/** Chip value that matches `disambiguateCrmFieldLabels` option values. */
export function fieldOptionValue(
  path: string,
  fields: CrmFieldOption[],
): string {
  return findCrmField(fields, path)?.handle ?? fieldHandleFromPath(path)
}

export const BOOLEAN_VALUE_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' },
]

/**
 * Operators offered for a CRM field. Stays within trigger-evaluator ops
 * (`eq` / `neq` / `gt` / `gte` / `lt` / `lte` / `contains` / `changed_from_to`).
 */
export function operatorsForField(
  field: CrmFieldOption | undefined,
  operation?: string | null,
): Array<{ value: ConditionOperator; label: string }> {
  const allowTransition = (operation ?? 'updated') === 'updated'
  const kind = valueKindForField(field)

  const allowed: ConditionOperator[] = (() => {
    switch (kind) {
      case 'enum':
        return ['eq', 'neq']
      case 'number':
        return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte']
      case 'boolean':
        return ['eq']
      case 'string':
      default:
        return ['eq', 'neq', 'contains']
    }
  })()

  if (allowTransition && kind !== undefined) {
    allowed.push('changed_from_to')
  }

  return FIELD_OPERATORS.filter((o) =>
    allowed.includes(o.value as ConditionOperator),
  ).map((o) => ({
    value: o.value as ConditionOperator,
    label: o.label,
  }))
}

export function coerceConditionValue(
  field: CrmFieldOption | undefined,
  raw: unknown,
  emptyAs: '' | null = '',
): unknown {
  if (raw === '' || raw == null) return emptyAs
  const kind = valueKindForField(field)
  if (kind === 'number') {
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) ? n : emptyAs
  }
  if (kind === 'boolean') {
    if (raw === true || raw === 'true') return true
    if (raw === false || raw === 'false') return false
    return emptyAs
  }
  return String(raw)
}

/**
 * Payload path for a CRM field condition.
 * - updated → data.after.{handle}
 * - created / deleted → data.record.{handle}
 */
export function crmFieldPayloadPath(
  handle: string,
  operation?: string | null,
): string {
  if (operation === 'updated') return `data.after.${handle}`
  return `data.record.${handle}`
}

/** Last segment of a dotted path (field handle) */
export function fieldHandleFromPath(path: string): string {
  const parts = path.split('.')
  return parts[parts.length - 1] || path
}

const CRM_INSTANCE_PATH_PREFIX =
  /^(data\.(?:record|after|before))\.[^.]+$/

/**
 * True when path is a CRM instance field path under data.record|after|before.
 * Thread / app paths (e.g. data.participant.kind) are left alone.
 */
export function isCrmInstanceFieldPath(path: string): boolean {
  return CRM_INSTANCE_PATH_PREFIX.test(path)
}

/**
 * Remap CRM field condition paths (and incompatible ops) when the CRM
 * operation changes between created / updated / deleted.
 *
 * - Paths stay keyed by field handle; only the record/after/before segment moves.
 * - `changed_from_to` requires an updated payload (changedFields/before/after).
 *   When leaving updated, convert to `eq` using the prior `to` value when set.
 */
export function remapCrmConditionsForOperation(
  conditions: TriggerCondition[],
  operation: string | null | undefined,
): TriggerCondition[] {
  return conditions.map((condition) => {
    if (!isCrmInstanceFieldPath(condition.path)) {
      return condition
    }

    const handle = fieldHandleFromPath(condition.path)
    const path = crmFieldPayloadPath(handle, operation)

    if (condition.kind === 'relative_date') {
      return path === condition.path ? condition : { ...condition, path }
    }

    // field condition
    if (condition.op === 'changed_from_to' && operation !== 'updated') {
      return {
        kind: 'field' as const,
        path,
        fieldLabel: condition.fieldLabel,
        op: 'eq' as const,
        value: condition.to ?? '',
      }
    }

    if (path === condition.path) {
      return condition
    }

    return { ...condition, path }
  })
}

/**
 * When multiple CRM fields share a display label, append the handle so pickers
 * stay unambiguous (e.g. two "Membership Status" fields → type vs membership_status).
 */
export function disambiguateCrmFieldLabels(
  fields: CrmFieldOption[],
): Array<{ value: string; label: string }> {
  const labelCounts = new Map<string, number>()
  for (const field of fields) {
    labelCounts.set(field.label, (labelCounts.get(field.label) ?? 0) + 1)
  }

  return fields.map((field) => ({
    value: field.handle,
    label:
      (labelCounts.get(field.label) ?? 0) > 1
        ? `${field.label} (${field.handle})`
        : field.label,
  }))
}

/** Title-case an entity key: "member" → "Member", "membership" → "Membership" */
export function titleCaseEntity(entity: string): string {
  if (!entity) return entity
  return entity.charAt(0).toUpperCase() + entity.slice(1).replace(/_/g, ' ')
}

/**
 * Group app catalog events by entity prefix.
 * `member.created` / `member.updated` → entity "member" labeled "Member"
 */
export function groupAppEventsByEntity(
  events: AppEventOption[],
): AppEntityOption[] {
  const byEntity = new Map<string, AppEntityOption>()

  for (const event of events) {
    const parts = event.name.split('.')
    const entity = parts[0] || event.name
    const op = parts.length > 1 ? parts[parts.length - 1]! : event.name

    let group = byEntity.get(entity)
    if (!group) {
      group = {
        entity,
        label: titleCaseEntity(entity),
        events: [],
        operations: [],
      }
      byEntity.set(entity, group)
    }

    group.events.push(event)
    if (!group.operations.some((o) => o.value === op)) {
      group.operations.push({
        value: op,
        label: op,
        eventName: event.name,
      })
    }
  }

  // Prefer created → updated → deleted order when present
  const opOrder = ['created', 'updated', 'deleted']
  for (const group of byEntity.values()) {
    group.operations.sort((a, b) => {
      const ai = opOrder.indexOf(a.value)
      const bi = opOrder.indexOf(b.value)
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
    })
  }

  return Array.from(byEntity.values()).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
}

/**
 * Pick default operation for an app entity: prefer "updated", else first available.
 */
export function pickDefaultAppOperation(entity: AppEntityOption): {
  value: string
  label: string
  eventName: string
} {
  const updated = entity.operations.find((o) => o.value === 'updated')
  return updated ?? entity.operations[0]!
}

/**
 * Pick default thread operation: prefer received for messages, else first.
 */
export function pickDefaultThreadOperation(entity: ThreadEntityOption) {
  const received = entity.operations.find((o) => o.value === 'received')
  const updated = entity.operations.find((o) => o.value === 'updated')
  return received ?? updated ?? entity.operations[0]!
}

export function getThreadEntityForEventType(
  eventType: string,
): ThreadEntityOption | undefined {
  return THREAD_ENTITIES.find((e) =>
    e.operations.some((op) => op.eventType === eventType),
  )
}

function isCrmOperation(value: string): value is CrmOperation {
  return value === 'created' || value === 'updated' || value === 'deleted'
}

/**
 * Build an editable matchConfig for a legacy subscription that only stored
 * eventType. Returns null when the event type is not a thread, CRM, or app event.
 */
export function matchConfigFromEventType(
  eventType: string,
): TriggerMatchConfig | null {
  const threadEntity = getThreadEntityForEventType(eventType)
  if (threadEntity) {
    return {
      version: 1,
      source: 'thread',
      eventType,
      label: threadEntity.label,
      conditions: [],
    }
  }

  if (eventType.startsWith('instance.')) {
    const operation = eventType.slice('instance.'.length)
    if (!isCrmOperation(operation)) return null
    return {
      version: 1,
      source: 'crm',
      eventType,
      label: 'Record',
      operation,
      conditions: [],
    }
  }

  if (eventType.startsWith('app.')) {
    const rest = eventType.slice('app.'.length)
    const splitAt = rest.indexOf('.')
    if (splitAt <= 0) return null
    const appHandle = rest.slice(0, splitAt)
    const appEventName = rest.slice(splitAt + 1)
    if (!appHandle || !appEventName) return null
    const entity = appEventName.split('.')[0] || appEventName
    const operation = appEventName.split('.').pop() ?? ''
    return {
      version: 1,
      source: 'app',
      eventType,
      label: titleCaseEntity(entity),
      appHandle,
      appEventName,
      operation: isCrmOperation(operation) ? operation : undefined,
      conditions: [],
    }
  }

  return null
}

export function getAppEntities(app: InstalledAppOption): AppEntityOption[] {
  return groupAppEventsByEntity(app.events)
}

/** Default event type for a participant kind (matches common DB subscriptions) */
export function eventTypeForParticipantKind(kind: string): string {
  return kind === 'CONTACT'
    ? 'thread.message.received'
    : 'thread.message.sent'
}

export function buildThreadKindCondition(
  kind: string,
): TriggerCondition {
  return {
    kind: 'field',
    path: THREAD_KIND_PATH,
    fieldLabel: 'Kind',
    op: 'eq',
    value: kind,
  }
}

export function buildParticipantKindCondition(
  kind: string,
): TriggerCondition {
  return {
    kind: 'field',
    path: PARTICIPANT_KIND_PATH,
    fieldLabel: 'Created by',
    op: 'eq',
    value: kind,
  }
}

/** Participant condition for attachment rows — same path, “Received from” label. */
export function buildAttachmentParticipantCondition(
  kind: string,
): TriggerCondition {
  return {
    kind: 'field',
    path: PARTICIPANT_KIND_PATH,
    fieldLabel: 'Received from',
    op: 'eq',
    value: kind,
  }
}

export function isAttachmentEventType(eventType?: string | null): boolean {
  return eventType === ATTACHMENT_RECEIVED_EVENT
}

/** Event-context tree node from `getEventContext` (path + optional children). */
export type EventContextFieldLike = {
  path?: string
  handle?: string
  label: string
  type?: string
  children?: EventContextFieldLike[]
}

const HIDDEN_THREAD_FIELD_SUFFIXES = ['.id', '.fileId', '.url']
const HIDDEN_ID_HANDLE = /(?:^|[.])[A-Za-z]*Id$/
const PREFERRED_ATTACHMENT_HANDLES = [
  'data.attachment.name',
  'data.attachment.mimeType',
  'data.attachment.size',
]

/**
 * Flatten event-context fields into picker options.
 * Keeps string/number/boolean leaves; hides IDs/URLs; prefers attachment name/MIME/size.
 */
export function flattenEventContextToFieldOptions(
  fields: EventContextFieldLike[],
): CrmFieldOption[] {
  const out: CrmFieldOption[] = []

  const walk = (list: EventContextFieldLike[]) => {
    for (const field of list) {
      if (field.children?.length) {
        walk(field.children)
        continue
      }
      const handle = field.path ?? field.handle ?? ''
      if (!handle) continue
      const type = (field.type ?? 'string').toLowerCase()
      if (type !== 'string' && type !== 'number' && type !== 'boolean') continue
      if (PRIMARY_THREAD_PATHS.has(handle)) continue
      if (HIDDEN_THREAD_FIELD_SUFFIXES.some((s) => handle.endsWith(s))) continue
      if (HIDDEN_ID_HANDLE.test(handle)) continue
      out.push({
        handle,
        label: field.label,
        type: field.type ?? 'string',
        hasOptions: false,
      })
    }
  }

  walk(fields)

  const preferred = PREFERRED_ATTACHMENT_HANDLES.map((h) =>
    out.find((f) => f.handle === h),
  ).filter((f): f is CrmFieldOption => Boolean(f))
  const rest = out.filter(
    (f) => !PREFERRED_ATTACHMENT_HANDLES.includes(f.handle),
  )
  return [...preferred, ...rest]
}

/** Extra-condition fields for a thread row (skip primary + already-used paths). */
export function threadAndPickerFields(
  fields: CrmFieldOption[],
  conditions: TriggerCondition[],
): CrmFieldOption[] {
  const used = new Set<string>(PRIMARY_THREAD_PATHS)
  for (const condition of conditions) {
    used.add(condition.path)
  }
  return fields.filter((field) => !used.has(field.handle))
}

/** Build a matchConfig from an Add Trigger menu selection */
export function buildMatchConfigFromMenuItem(
  item: TriggerMenuItem,
): TriggerMatchConfig {
  const eventType =
    item.eventType ||
    (item.modelHandle
      ? 'instance.updated'
      : item.appHandle
        ? `app.${item.appHandle}.${item.appEventName}`
        : 'thread.created')

  return {
    version: 1,
    source: item.source,
    eventType,
    label: item.label,
    model: item.modelHandle,
    operation:
      item.source === 'crm' || item.source === 'app'
        ? ((item.operation as CrmOperation) ?? 'updated')
        : undefined,
    appHandle: item.appHandle,
    appEventName: item.appEventName,
    conditions: item.initialConditions ?? [],
  }
}

/** Whether a condition is the primary thread-kind / participant chip */
export function isPrimaryThreadCondition(cond: TriggerCondition): boolean {
  return (
    cond.kind === 'field' &&
    (cond.path === THREAD_KIND_PATH || cond.path === PARTICIPANT_KIND_PATH)
  )
}

export function configsEqual(
  a: TriggerMatchConfig | null | undefined,
  b: TriggerMatchConfig | null | undefined,
): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

// Build sentence from matchConfig
export function buildSentence(config: TriggerMatchConfig): string {
  const parts: string[] = ['When']

  if (config.source === 'thread') {
    const entity = getThreadEntityForEventType(config.eventType)
    const kindCond = config.conditions.find(
      (c) => c.kind === 'field' && c.path === THREAD_KIND_PATH,
    )
    const participantCond = config.conditions.find(
      (c) => c.kind === 'field' && c.path === PARTICIPANT_KIND_PATH,
    )

    if (entity?.id === 'thread' || config.eventType === 'thread.created') {
      const kindValue =
        kindCond && kindCond.kind === 'field'
          ? String(kindCond.value ?? 'CONVERSATION')
          : 'CONVERSATION'
      const kindLabel =
        THREAD_KINDS.find((k) => k.value === kindValue)?.label ?? kindValue
      const kindArticle = /^[aeiou]/i.test(kindLabel) ? 'an' : 'a'
      parts.push(`a Thread is ${kindArticle} ${kindLabel}`)
    } else if (
      entity?.id === 'attachment' ||
      isAttachmentEventType(config.eventType)
    ) {
      const kindValue =
        participantCond && participantCond.kind === 'field'
          ? String(participantCond.value ?? '')
          : ''
      if (kindValue) {
        const kindLabel =
          PARTICIPANT_KINDS.find((k) => k.value === kindValue)?.label ??
          kindValue
        parts.push(`an attachment is received from ${kindLabel}`)
      } else {
        parts.push('an attachment is received')
      }
    } else if (entity?.id === 'message' || participantCond) {
      const kindValue =
        participantCond && participantCond.kind === 'field'
          ? String(participantCond.value ?? 'CONTACT')
          : 'CONTACT'
      const kindLabel =
        PARTICIPANT_KINDS.find((k) => k.value === kindValue)?.label ?? kindValue
      parts.push(`a message is Created by ${kindLabel}`)
    } else if (entity) {
      const op =
        entity.operations.find((o) => o.eventType === config.eventType) ??
        entity.operations[0]
      const article = /^[aeiou]/i.test(entity.label) ? 'an' : 'a'
      parts.push(`${article} ${entity.label.toLowerCase()} is ${op?.label}`)
    } else {
      parts.push(config.label)
    }
  } else if (config.source === 'crm') {
    const article = /^[aeiou]/i.test(config.label) ? 'an' : 'a'
    const copula = primaryCopula(config.operation)
    parts.push(
      `${article} ${config.label} ${copula} ${config.operation ?? 'updated'}`,
    )
  } else if (config.source === 'app') {
    const article = /^[aeiou]/i.test(config.label) ? 'an' : 'a'
    const op =
      config.operation ?? config.appEventName?.split('.').pop() ?? 'updated'
    const copula = primaryCopula(op)
    parts.push(`${article} ${config.label.toLowerCase()} ${copula} ${op}`)
  }

  for (const condition of config.conditions) {
    if (isPrimaryThreadCondition(condition)) continue

    if (condition.kind === 'relative_date') {
      const dir = condition.direction ?? 'past'
      const opLabel = getOperatorLabel(toBeforeAfterOp(condition.op))
      parts.push(
        dir === 'past' ? 'and was' : 'and is',
        condition.fieldLabel,
        opLabel,
        formatRelativeDate(condition.amount, condition.unit, dir),
      )
    } else if (condition.kind === 'field') {
      parts.push(config.source === 'thread' ? 'and' : 'and the', condition.fieldLabel)
      const opLabel = getOperatorLabel(condition.op)
      if (condition.op === 'changed_from_to') {
        const fromLabel =
          condition.from == null ? 'Any' : String(condition.from)
        const toLabel = condition.to == null ? 'Any' : String(condition.to)
        parts.push(`${opLabel} ${fromLabel} to ${toLabel}`)
      } else {
        parts.push(`${opLabel} ${String(condition.value ?? '')}`)
      }
    }
  }

  return parts.join(' ')
}

export function parseLegacyCondition(
  condition: string,
): { path: string; op: string; value: string } | null {
  const match = condition.match(
    /\{\{\s*([\w.]+)\s*(==|!=|contains)\s*['"]?([^'"}\s]+)['"]?\s*\}\}/,
  )
  if (match) {
    return {
      path: match[1]!,
      op: match[2] === '==' ? 'eq' : match[2] === '!=' ? 'neq' : 'contains',
      value: match[3]!,
    }
  }
  return null
}

export function buildLegacySummary(
  eventType: string,
  condition?: string | null,
): string {
  const parts: string[] = []

  const threadEntity = getThreadEntityForEventType(eventType)
  if (threadEntity) {
    const op = threadEntity.operations.find((o) => o.eventType === eventType)
    const article = /^[aeiou]/i.test(threadEntity.label) ? 'an' : 'a'
    parts.push(
      `When ${article} ${threadEntity.label.toLowerCase()} is ${op?.label ?? 'created'}`,
    )
  } else if (eventType.startsWith('app.')) {
    const appParts = eventType.split('.')
    parts.push(`When ${appParts.slice(2).join('.')} on ${appParts[1]}`)
  } else if (eventType.startsWith('instance.')) {
    const op = eventType.split('.')[1]
    parts.push(`When instance ${op}`)
  } else {
    parts.push(`When ${eventType}`)
  }

  if (condition) {
    const parsed = parseLegacyCondition(condition)
    if (parsed) {
      const opLabel =
        parsed.op === 'eq'
          ? 'is'
          : parsed.op === 'neq'
            ? 'is not'
            : 'contains'
      parts.push(`and ${parsed.path} ${opLabel} ${parsed.value}`)
    } else {
      parts.push(`(${condition})`)
    }
  }

  return parts.join(' ')
}
